import { supabase } from '../lib/supabase'
import type { ShootDayContactKind } from './shootDaySource'

/**
 * People who have been typed onto a call sheet before.
 *
 * Freelance crew, client-side contacts and featured guests have no Portal OS
 * account, so until 24 Sep 2026 every call sheet meant retyping the same
 * names, roles and phone numbers. Twenty-two freelancers were already in the
 * database by then, each one typed by hand, some of them more than once.
 *
 * There is no list to maintain and no new table: the suggestions ARE the
 * history, read back. Same principle as the phone number saved against a
 * staff member — the record is a by-product of the work, never a second chore.
 * A separate "crew database" inside Portal OS would be a third place for the
 * same people to live (the Crew Directory is the second), and it would be
 * out of date within a month.
 *
 * One honest limit: `set_shoot_day_contacts` replaces a sheet's whole list on
 * every save, so somebody taken off every call sheet stops being suggested.
 * This is "people currently on a call sheet somewhere", not "everyone ever
 * typed". It self-cleans a typo, and a freelancer you have not booked for a
 * year still appears as long as their last sheet exists.
 */

export interface KnownContact {
  name: string
  role: string
  phone: string
  email: string
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

/**
 * Everyone previously typed into this kind of list, most recently used first.
 *
 * Returns an empty list rather than throwing: a call sheet that cannot reach
 * the suggestions is simply a call sheet you type by hand, which is exactly
 * what it was before.
 */
export async function fetchKnownContacts(kind: ShootDayContactKind): Promise<KnownContact[]> {
  if (!supabase) return []

  const { data, error } = await supabase.rpc('list_known_contacts', { p_kind: kind })
  if (error) {
    console.warn(`[supabase] list_known_contacts(${kind}) failed: ${error.message}`)
    return []
  }

  return ((data ?? []) as Record<string, unknown>[])
    .map((row) => ({
      name: text(row.name),
      role: text(row.role),
      phone: text(row.phone),
      email: text(row.email),
    }))
    .filter((person) => person.name !== '')
}

/**
 * The person a typed name refers to, if any. Case- and space-insensitive,
 * because "jack" and "Jack " are the same freelancer.
 */
export function matchKnownContact(
  known: KnownContact[],
  typed: string,
): KnownContact | undefined {
  const needle = typed.trim().toLowerCase()
  if (!needle) return undefined
  return known.find((person) => person.name.trim().toLowerCase() === needle)
}
